// ─── PossibleMatchPanel.tsx ───────────────────────────────────────────────────
// Slide-over panel for reviewing WinTeam invoice candidates for a Possible Match
// transaction and either confirming or rejecting the match.
//
// Props:
//   transaction  — the Possible Match transaction to review
//   onConfirmed  — called with updated CCReconTransaction after confirm
//   onRejected   — called with updated CCReconTransaction after reject
//   onClose      — called when panel is dismissed without action
//
// State owned here:
//   candidates   — fetched from GET /cc-recon/transactions/{id}/matches on mount
//   loading      — true while fetching
//   confirming   — invoice_number currently being confirmed (shows spinner)
//   rejecting    — true while reject is in flight
//   error        — fetch or action error message

import React, { useState, useEffect, useCallback } from 'react';
import type { CCReconTransaction, CCWinTeamInvoiceCandidate } from '../../types/ccTypes';
import { getTransactionMatches, confirmMatch, rejectMatch } from '../../api/ccReconApi';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtAmt(n: number): string {
  return '$' + Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  const parts = iso.split('-');
  if (parts.length < 3) return iso;
  const [y, m, d] = parts;
  return `${m}/${d}/${y.slice(2)}`;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  transaction: CCReconTransaction;
  onConfirmed: (updated: CCReconTransaction) => void;
  onRejected:  (updated: CCReconTransaction) => void;
  onClose:     () => void;
}

// ── Inline style helpers ──────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#888',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.3px',
};

const valueStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#1a1a1a',
  fontWeight: 500,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function PossibleMatchPanel({
  transaction,
  onConfirmed,
  onRejected,
  onClose,
}: Props) {
  const [candidates, setCandidates]   = useState<CCWinTeamInvoiceCandidate[]>([]);
  const [loading, setLoading]         = useState(true);
  const [fetchError, setFetchError]   = useState<string | null>(null);
  const [confirming, setConfirming]   = useState<string | null>(null); // invoice_number
  const [rejecting, setRejecting]     = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // ── Fetch candidates on mount / transaction change ──────────────────────────

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setCandidates([]);
    try {
      const resp = await getTransactionMatches(transaction.id);
      setCandidates(resp.candidates);
    } catch (err: any) {
      setFetchError(
        err?.response?.data?.error ||
        'Failed to load WinTeam candidates — check network and try again'
      );
    } finally {
      setLoading(false);
    }
  }, [transaction.id]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleConfirm = async (candidate: CCWinTeamInvoiceCandidate) => {
    setConfirming(candidate.invoice_number);
    setActionError(null);
    try {
      const updated = await confirmMatch(transaction.id, candidate.invoice_number);
      onConfirmed(updated);
    } catch (err: any) {
      setActionError(
        err?.response?.data?.error || 'Failed to confirm match — please try again'
      );
      setConfirming(null);
    }
  };

  const handleReject = async () => {
    setRejecting(true);
    setActionError(null);
    try {
      const updated = await rejectMatch(transaction.id);
      onRejected(updated);
    } catch (err: any) {
      setActionError(
        err?.response?.data?.error || 'Failed to reject match — please try again'
      );
      setRejecting(false);
    }
  };

  const busy = !!confirming || rejecting;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Dark overlay */}
      <div
        onClick={busy ? undefined : onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 200,
        }}
      />

      {/* Panel — wider to accommodate GL distributions */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '580px',
        backgroundColor: 'white',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
        zIndex: 201,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Segoe UI, sans-serif',
      }}>

        {/* ── Panel header ──────────────────────────────────────────────────── */}
        <div style={{
          padding: '18px 20px 14px',
          borderBottom: '1px solid #e8eaf0',
          backgroundColor: '#f8f9ff',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '12px',
          }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>
                Review Possible Match
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                Select the WinTeam invoice that matches this transaction
              </div>
            </div>
            <button
              onClick={busy ? undefined : onClose}
              disabled={busy}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                color: '#888',
                cursor: busy ? 'default' : 'pointer',
                lineHeight: 1,
                padding: '2px 4px',
              }}
            >
              ×
            </button>
          </div>

          {/* Transaction summary card */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e0e7ff',
            borderRadius: '6px',
            padding: '12px 14px',
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 700,
              color: '#1a1a1a',
              marginBottom: '6px',
              wordBreak: 'break-word',
            }}>
              {transaction.description}
            </div>
            <div style={{
              display: 'flex',
              gap: '20px',
              flexWrap: 'wrap',
              fontSize: '12px',
              color: '#555',
            }}>
              <span>
                <span style={{ color: '#888' }}>Amount: </span>
                <strong style={{ color: '#1a1a1a' }}>{fmtAmt(transaction.invoice_amount)}</strong>
              </span>
              <span>
                <span style={{ color: '#888' }}>Post date: </span>
                <strong style={{ color: '#1a1a1a' }}>{fmtDate(transaction.post_date)}</strong>
              </span>
              <span>
                <span style={{ color: '#888' }}>Card: </span>
                <strong style={{ color: '#1a1a1a' }}>••••{transaction.card_last4}</strong>
              </span>
              <span>
                <span style={{ color: '#888' }}>Vendor: </span>
                <strong style={{ color: '#1a1a1a' }}>{transaction.vendor_number}</strong>
              </span>
              <span>
                <span style={{ color: '#888' }}>GL: </span>
                <strong style={{ color: '#1a1a1a' }}>{transaction.gl_number}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* ── Scrollable candidates body ─────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {loading && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#888',
              fontSize: '14px',
            }}>
              <div style={{ fontSize: '28px', marginBottom: '12px' }}>⏳</div>
              Searching WinTeam for matching invoices…
            </div>
          )}

          {!loading && fetchError && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#c0392b',
              fontSize: '13px',
            }}>
              <div style={{ fontSize: '24px', marginBottom: '10px' }}>⚠</div>
              <div style={{ marginBottom: '14px' }}>{fetchError}</div>
              <button
                onClick={fetchCandidates}
                style={{
                  padding: '7px 18px',
                  backgroundColor: '#c0392b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !fetchError && candidates.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '50px 20px',
              color: '#888',
              fontSize: '13px',
            }}>
              <div style={{ fontSize: '28px', marginBottom: '12px' }}>🔍</div>
              <div style={{ fontWeight: 600, color: '#555', marginBottom: '6px', fontSize: '14px' }}>
                No matching WinTeam invoices found
              </div>
              <div style={{ marginBottom: '6px' }}>
                No CreditCard invoices within ±7 days match{' '}
                <strong>{fmtAmt(transaction.invoice_amount)}</strong>.
              </div>
              <div style={{ fontSize: '12px', color: '#aaa' }}>
                This transaction may not have been entered in WinTeam yet —
                use Convert to post it.
              </div>
            </div>
          )}

          {!loading && !fetchError && candidates.length > 0 && (
            <>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px', fontWeight: 600 }}>
                {candidates.length} candidate invoice{candidates.length !== 1 ? 's' : ''} found
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {candidates.map(candidate => {
                  const isConfirming = confirming === candidate.invoice_number;
                  return (
                    <div
                      key={candidate.invoice_number}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '7px',
                        backgroundColor: '#fafafa',
                        overflow: 'hidden',
                      }}
                    >
                      {/* ── Invoice header: number + amount ──────────────── */}
                      <div style={{
                        padding: '14px 16px 10px',
                        borderBottom: '1px solid #f0f0f0',
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '4px',
                        }}>
                          <div>
                            <div style={{
                              fontSize: '15px',
                              fontWeight: 700,
                              color: '#00386C',
                              fontFamily: 'monospace',
                            }}>
                              {candidate.invoice_number || '(no number)'}
                            </div>
                            {candidate.memo_line1 && (
                              <div style={{
                                fontSize: '12px',
                                color: '#555',
                                marginTop: '3px',
                                wordBreak: 'break-word',
                              }}>
                                {candidate.memo_line1}
                              </div>
                            )}
                          </div>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: 700,
                            color: '#1a1a1a',
                            whiteSpace: 'nowrap',
                            marginLeft: '12px',
                          }}>
                            {fmtAmt(candidate.invoice_amount)}
                          </div>
                        </div>
                      </div>

                      {/* ── Detail fields ────────────────────────────────── */}
                      <div style={{
                        padding: '10px 16px',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '10px 16px',
                        borderBottom: '1px solid #f0f0f0',
                      }}>
                        <div>
                          <div style={labelStyle}>Invoice Date</div>
                          <div style={valueStyle}>{fmtDate(candidate.invoice_date)}</div>
                        </div>
                        <div>
                          <div style={labelStyle}>Posting Date</div>
                          <div style={valueStyle}>{fmtDate(candidate.posting_date)}</div>
                        </div>
                        <div>
                          <div style={labelStyle}>Due Date</div>
                          <div style={valueStyle}>{fmtDate(candidate.due_date)}</div>
                        </div>
                        <div>
                          <div style={labelStyle}>Vendor #</div>
                          <div style={valueStyle}>{candidate.vendor_number || '—'}</div>
                        </div>
                        <div>
                          <div style={labelStyle}>Company #</div>
                          <div style={valueStyle}>{candidate.company_number || '—'}</div>
                        </div>
                        <div>
                          <div style={labelStyle}>Payment Method ID</div>
                          <div style={valueStyle}>
                            {candidate.payment_method_id || candidate.payment_method || '—'}
                          </div>
                        </div>
                        <div>
                          <div style={labelStyle}>Credit Card Vendor #</div>
                          <div style={valueStyle}>{candidate.credit_card_vendor_number || '—'}</div>
                        </div>
                      </div>

                      {/* ── GL Distributions ─────────────────────────────── */}
                      {candidate.gl_distributions && candidate.gl_distributions.length > 0 && (
                        <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0' }}>
                          <div style={{ ...labelStyle, marginBottom: '6px' }}>GL Distributions</div>
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '12px',
                          }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <th style={{ padding: '3px 8px 3px 0', textAlign: 'left', color: '#888', fontWeight: 600 }}>Account</th>
                                <th style={{ padding: '3px 8px', textAlign: 'left', color: '#888', fontWeight: 600 }}>Job</th>
                                <th style={{ padding: '3px 8px', textAlign: 'right', color: '#888', fontWeight: 600 }}>Amount</th>
                                <th style={{ padding: '3px 0 3px 8px', textAlign: 'left', color: '#888', fontWeight: 600 }}>Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {candidate.gl_distributions.map((gl, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                  <td style={{ padding: '4px 8px 4px 0', color: '#1a1a1a', fontWeight: 600, fontFamily: 'monospace' }}>
                                    {gl.account_number || '—'}
                                  </td>
                                  <td style={{ padding: '4px 8px', color: '#444', fontFamily: 'monospace' }}>
                                    {gl.job_number || '—'}
                                  </td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', color: '#1a1a1a', fontFamily: 'monospace' }}>
                                    {fmtAmt(gl.amount)}
                                  </td>
                                  <td style={{
                                    padding: '4px 0 4px 8px',
                                    color: '#666',
                                    maxWidth: '160px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={gl.notes}
                                  >
                                    {gl.notes || '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* ── Confirm button ───────────────────────────────── */}
                      <div style={{ padding: '10px 16px 14px' }}>
                        <button
                          onClick={() => handleConfirm(candidate)}
                          disabled={busy}
                          style={{
                            width: '100%',
                            padding: '8px',
                            fontSize: '13px',
                            fontWeight: 700,
                            backgroundColor: busy ? '#e0e0e0' : '#1b5e20',
                            color: busy ? '#888' : 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: busy ? 'default' : 'pointer',
                          }}
                        >
                          {isConfirming ? 'Confirming…' : '✓ Confirm This Match'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* ── Action error ───────────────────────────────────────────────────── */}
        {actionError && (
          <div style={{
            padding: '10px 20px',
            backgroundColor: '#fdecea',
            borderTop: '1px solid #fca5a5',
            color: '#c0392b',
            fontSize: '12px',
          }}>
            ⚠ {actionError}
          </div>
        )}

        {/* ── Footer ────────────────────────────────────────────────────────── */}
        <div style={{
          padding: '14px 20px',
          borderTop: '1px solid #e8eaf0',
          backgroundColor: '#f8f9ff',
          display: 'flex',
          gap: '10px',
        }}>
          <button
            onClick={handleReject}
            disabled={busy}
            style={{
              flex: 1,
              padding: '9px',
              fontSize: '13px',
              fontWeight: 600,
              backgroundColor: busy ? '#e0e0e0' : 'white',
              color: busy ? '#888' : '#c0392b',
              border: `1px solid ${busy ? '#ccc' : '#fca5a5'}`,
              borderRadius: '5px',
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            {rejecting ? 'Returning…' : '✕ Not a Match — Return to Grid'}
          </button>
          <button
            onClick={busy ? undefined : onClose}
            disabled={busy}
            style={{
              padding: '9px 18px',
              fontSize: '13px',
              fontWeight: 600,
              backgroundColor: busy ? '#e0e0e0' : '#f0f2f5',
              color: busy ? '#888' : '#444',
              border: '1px solid #cbd5e0',
              borderRadius: '5px',
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
        </div>

      </div>
    </>
  );
}

